// PlanView.js

import React, { useEffect, useState } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { text as textColors, border } from '../../utils/darkModeAdapter';

import LoadingScreen from '../../components/LoadingScreen';
import DifficultyCard from './PlanComponents/DifficultyCard';
import OverviewCard from './PlanComponents/OverviewCard';
import RecommendedHiresCard from './PlanComponents/RecommendedHiresCard';
import RecruitsPeriodCard from './PlanComponents/RecruitsPeriodCard';
import BudgetDistributionCard from './PlanComponents/BudgetDistributionCard';
import RecommendedSkillsCard from './PlanComponents/RecommendedSkillsCard';
import ScheduleFlowGantt from './PlanComponents/ScheduleFlowGantt';
import FunnelVisualizationCard from './PlanComponents/FunnelVisualizationCard';
import CostAnalysisCard from './PlanComponents/CostAnalysisCard';

// セクションヘッダー
function SectionLabel({ label }) {
  const theme = useTheme();
  return (
    <Grid item xs={12}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: -1, mt: 1 }}>
        <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#f37335' }} />
        <Typography variant="subtitle2" sx={{
          fontWeight: 700, color: textColors(theme).secondary,
          fontSize: '0.8rem', letterSpacing: '0.05em',
        }}>
          {label}
        </Typography>
        <Box sx={{ flex: 1, height: 1, backgroundColor: border(theme).light }} />
      </Box>
    </Grid>
  );
}

// PlanView本体
export default function PlanView({ planData, budgetYen, requirements, onRegeneratePlan }) {
  const [regenerating, setRegenerating] = useState(false);
  const [activeField, setActiveField] = useState(null);

  // planDataが変更されたらregenerating状態をリセット
  useEffect(() => {
    if (regenerating) {
      setRegenerating(false);
    }
  }, [planData]);

  // エラー時にローディングステートをリセットするためのイベントリスナー
  useEffect(() => {
    const handleRegenerateError = () => {
      if (regenerating) {
        setRegenerating(false);
      }
    };

    window.addEventListener('plan_regenerate_error', handleRegenerateError);

    return () => {
      window.removeEventListener('plan_regenerate_error', handleRegenerateError);
    };
  }, [regenerating]);

  if (!planData) {
    return <LoadingScreen variant="inline" message="データを読み込み中..." featureKey="recruit_plan" showSteps={false} />;
  }
  const finalBudget = parseInt(budgetYen ?? "0", 10);

  const adv = planData.advices || {};

  // requirementsから追加情報を取得
  const startDate = requirements?.startDate || null;
  const endDate = requirements?.endDate || null;
  const targetHires = requirements?.targetHires || 1;

  // バックエンド計算済みデータ
  const hiringMetrics = planData.hiringMetrics || null;
  const funnelData = planData.recommendedHiresAndApplicants?.funnel || null;
  const requiredApplicants = planData.recommendedHiresAndApplicants?.requiredApplicants || null;
  const successProbability = planData.successProbability || null;

  // 特定フィールドの値を変更して再生成するハンドラー
  const handleRegeneratePlan = (field, value) => {
    setActiveField(field);
    setRegenerating(true);
    onRegeneratePlan && onRegeneratePlan(field, value);
  };

  // フィールド名を日本語表示名に変換する関数
  const getFieldDisplayName = (fieldName) => {
    const fieldNameMap = {
      'difficulty': '採用難易度',
      'overview': '概要',
      'recommendedHiresAndApplicants': '推奨採用人数',
      'recruitsPeriod': '採用期間',
      'budgetDistribution': '予算配分',
      'recommendedSkills': '推奨スキル',
      'timeline': 'スケジュール'
    };
    return fieldNameMap[fieldName] || fieldName;
  };

  return (
    <Box
      sx={{
        my: 'auto',
        mx: 'auto',
        width: '100%',
        maxWidth: { xs: 360, sm: 600, md: 1800 },
        position: 'relative'
      }}
    >
      {/* ローディングオーバーレイ */}
      {regenerating && (
        <LoadingScreen
          variant="overlay"
          message={activeField ? `${getFieldDisplayName(activeField)}を更新中...` : '採用計画を再生成中...'}
          description="AIが最適な内容を分析しています。少々お待ちください..."
          loadingType="recruit_plan"
          featureKey="recruit_plan"
        />
      )}

      {/* グリッドレイアウト */}
      <Grid container spacing={3}>

        {/* ━━ 難易度・概要 ━━ */}
        <SectionLabel label="難易度・概要" />

        <Grid
          item
          xs={12}
          component={motion.div}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0 }}
        >
          <DifficultyCard
            difficulty={planData.difficulty}
            adv={adv}
            onRegenerate={handleRegeneratePlan}
            isRegenerating={regenerating && activeField === 'difficulty'}
            successProbability={successProbability}
          />
        </Grid>

        <Grid
          item
          xs={12}
          component={motion.div}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
        >
          <OverviewCard
            overview={planData.overview}
            adv={adv}
            onRegenerate={handleRegeneratePlan}
            isRegenerating={regenerating && activeField === 'overview'}
          />
        </Grid>

        {/* ━━ 採用計画 ━━ */}
        <SectionLabel label="採用計画" />

        <Grid
          item
          xs={12}
          md={6}
          component={motion.div}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
        >
          <RecommendedHiresCard
            data={planData.recommendedHiresAndApplicants}
            adv={adv}
          />
        </Grid>

        <Grid
          item
          xs={12}
          md={6}
          component={motion.div}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.3 }}
        >
          <RecruitsPeriodCard
            recruitsPeriod={planData.recruitsPeriod}
            adv={adv}
            onRegenerate={handleRegeneratePlan}
            isRegenerating={regenerating && activeField === 'recruitsPeriod'}
            startDate={startDate}
            endDate={endDate}
            hiringMetrics={hiringMetrics}
          />
        </Grid>

        {funnelData && (
          <Grid
            item
            xs={12}
            component={motion.div}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.35 }}
          >
            <FunnelVisualizationCard
              funnelData={funnelData}
              targetHires={targetHires}
              requiredApplicants={requiredApplicants}
              adv={adv}
            />
          </Grid>
        )}

        {/* ━━ 予算・コスト ━━ */}
        <SectionLabel label="予算・コスト" />

        <Grid
          item
          xs={12}
          md={6}
          component={motion.div}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.4 }}
        >
          <BudgetDistributionCard
            distribution={planData.budgetDistribution}
            adv={planData.advices}
            totalBudgetYen={finalBudget}
            onRegenerate={handleRegeneratePlan}
            isRegenerating={regenerating && activeField === 'budgetDistribution'}
          />
        </Grid>

        {finalBudget > 0 && (
          <Grid
            item
            xs={12}
            md={6}
            component={motion.div}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.45 }}
          >
            <CostAnalysisCard
              totalBudgetYen={finalBudget}
              targetHires={targetHires}
              distribution={planData.budgetDistribution}
              hiringMetrics={hiringMetrics}
              startDate={startDate}
              endDate={endDate}
              adv={adv}
            />
          </Grid>
        )}

        {/* ━━ スキル・スケジュール ━━ */}
        <SectionLabel label="スキル・スケジュール" />

        <Grid
          item
          xs={12}
          component={motion.div}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.5 }}
        >
          <RecommendedSkillsCard
            skills={planData.recommendedSkills}
            adv={adv}
            onRegenerate={handleRegeneratePlan}
            isRegenerating={regenerating && activeField === 'recommendedSkills'}
          />
        </Grid>

        <Grid
          item
          xs={12}
          component={motion.div}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.6 }}
        >
          <ScheduleFlowGantt
            timeline={planData.timeline}
            adv={adv}
            onRegenerate={handleRegeneratePlan}
            isRegenerating={regenerating && activeField === 'timeline'}
          />
        </Grid>
      </Grid>
    </Box>
  );
}
